/**
 * Page détail / édition d'un utilisateur — backoffice admin.
 *
 * Actions disponibles selon le rôle de l'acteur et l'état du user cible :
 *   - Modifier prenom/nom/téléphone/rôle (PATCH)
 *   - Suspendre / réactiver
 *   - Déverrouiller (si compte locked)
 *   - Forcer reset mdp (envoie le mail au user)
 *   - Supprimer (anonymisation RGPD avec confirmation)
 *
 * Refonte R9 (mai 2026) :
 *   - Lien retour + en-tête de page avec LogoMark UserCog + identité
 *   - Card glass "Profil" avec form 2 cols inputs glass
 *   - Card glass "Actions" avec boutons R2 (variants secondary, success pour réactivation)
 *   - Card glass "Zone dangereuse" avec Alert warning de confirmation + Button danger
 *
 * Logique métier 100% préservée :
 *   - api.admin.users.get/update/suspend/reactivate/unlock/forcePasswordReset/delete
 *   - Garde-fous : isSelf, isLocked, isDeleted, isSuperAdminTarget, actorIsSuperAdmin
 *   - canModifyTarget = !isSelf && !isDeleted && (super_admin || !targetSuperAdmin)
 *   - canEdit = super_admin | admin
 *   - Notification timer 4s, handleError pour ApiException
 *   - Promotion super_admin disabled pour non-super_admin (option select disabled)
 *   - confirmDelete 2-step avec Alert warning
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card, Button, Input, Field, Badge, Alert, LogoMark, SectionEyebrow,
} from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import {
  ArrowLeft, Save, Pause, Play, Unlock, KeyRound, Trash2, AlertTriangle,
  UserCog, AlertCircle, CheckCircle2,
} from 'lucide-react';
import type { AdminUserDetail, UserRole, UserService } from '@roronoa-collectivites/shared-types';

const ALL_ROLES: UserRole[] = ['super_admin', 'admin', 'manager', 'agent', 'administre'];

const SERVICE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '— Aucun (transverse) —' },
  { value: 'service_technique', label: 'Service technique' },
  { value: 'scolaire_cantine', label: 'Scolaire & cantine' },
  { value: 'petite_enfance', label: 'Petite enfance' },
  { value: 'urbanisme', label: 'Urbanisme' },
  { value: 'associations', label: 'Associations' },
  { value: 'grc', label: 'Accueil / GRC' },
  { value: 'rh', label: 'Ressources humaines' },
  { value: 'comptable', label: 'Comptabilité' },
];

const ROLE_LABEL: Record<UserRole, string> = {
  super_admin: 'Super admin',
  admin: 'Admin',
  manager: 'Manager',
  agent: 'Agent',
  administre: 'Administré',
};

/**
 * Couleur du badge = couleur de la BRIQUE (service) de l'agent, repli sur le rôle
 * pour les comptes transverses (admin/maire, super_admin, administré sans service).
 * Aligné sur UsersListPage pour une cohérence visuelle dans tout le backoffice.
 */
type Accent = 'tech' | 'asso' | 'urba' | 'grc' | 'cantine' | 'petite_enfance' | 'rh' | 'compta' | 'neutral';

const SERVICE_ACCENT: Record<UserService, Accent> = {
  service_technique: 'tech',
  scolaire_cantine: 'cantine',
  petite_enfance: 'petite_enfance',
  urbanisme: 'urba',
  associations: 'asso',
  grc: 'grc',
  rh: 'rh',
  comptable: 'compta',
};

const SERVICE_LABEL: Record<UserService, string> = {
  service_technique: 'Technique',
  scolaire_cantine: 'Scolaire / cantine',
  petite_enfance: 'Petite enfance',
  urbanisme: 'Urbanisme',
  associations: 'Associations',
  grc: 'Communication',
  rh: 'RH',
  comptable: 'Comptabilité',
};

const ROLE_ACCENT_FALLBACK: Record<UserRole, Accent> = {
  super_admin: 'urba',
  admin: 'tech',
  manager: 'neutral',
  agent: 'neutral',
  administre: 'neutral',
};

function userAccent(u: { role: UserRole; service: UserService | null }): Accent {
  if (u.service) return SERVICE_ACCENT[u.service];
  return ROLE_ACCENT_FALLBACK[u.role];
}

function userBadgeLabel(u: { role: UserRole; service: UserService | null }): string {
  const r = ROLE_LABEL[u.role];
  if (u.service) return `${r} · ${SERVICE_LABEL[u.service]}`;
  return r;
}

const SELECT_CLS = 'h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong disabled:opacity-60 disabled:cursor-not-allowed';

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: actor, baseRole } = useAuth();

  // L13.14 — la promotion de rôle est une action de GOUVERNANCE réservée aux
  // admins NATIFS (maire / adjoint) et au super_admin. Un utilisateur lui-même
  // promu admin via un override ne doit pas pouvoir promouvoir d'autres comptes.
  const canGrantRoles = baseRole === 'admin' || baseRole === 'super_admin';

  const [target, setTarget] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // form state
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [role, setRole] = useState<UserRole>('agent');
  const [service, setService] = useState<string>('');
  const [servicesAdditionnels, setServicesAdditionnels] = useState<UserService[]>([]);
  const [comptableBriques, setComptableBriques] = useState<('grc' | 'rh')[]>([]);
  const [poiGestion, setPoiGestion] = useState(false);
  // E3 — Périmètre d'administration (multi-briques) pour le rôle admin.
  const [adminBriques, setAdminBriques] = useState<string[]>([]);
  const [adminVueMaire, setAdminVueMaire] = useState(true);
  const [adminParametres, setAdminParametres] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchUser = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const u = await api.admin.users.get(id);
      setTarget(u);
      setPrenom(u.prenom);
      setNom(u.nom);
      setEmail(u.email);
      setTelephone(u.telephone ?? '');
      setRole(u.role);
      setService(u.service ?? '');
      setServicesAdditionnels((u.servicesAdditionnels ?? []) as UserService[]);
      setComptableBriques((u.comptableBriques ?? []) as ('grc' | 'rh')[]);
      setPoiGestion(u.poiGestion ?? false);
      // E3 — charge le périmètre si la cible est un admin (non super_admin).
      if (u.role === 'admin') {
        try {
          const { admins } = await api.admin.perimetres.list();
          const p = admins.find((a) => a.userId === u.id);
          setAdminBriques(p?.briques ?? []);
          setAdminVueMaire(p?.vueMaire ?? true);
          setAdminParametres(p?.parametres ?? true);
        } catch { /* périmètre non chargé : valeurs par défaut (admin complet) */ }
      } else {
        setAdminBriques([]);
        setAdminVueMaire(true);
        setAdminParametres(true);
      }
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.payload.error || 'Utilisateur introuvable');
      } else {
        setError('Erreur réseau');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  function notify(msg: string) {
    setSuccess(msg);
    setError(null);
    setTimeout(() => setSuccess(null), 4000);
  }

  function handleError(err: unknown) {
    if (err instanceof ApiException) {
      setError(err.payload.error || 'Action refusée');
    } else {
      setError('Erreur réseau');
    }
    setSuccess(null);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    try {
      const updated = await api.admin.users.update(id, {
        prenom,
        nom,
        email: email.trim(),
        telephone: telephone.trim() === '' ? null : telephone,
        role,
        service: (service.trim() === '' ? null : service) as UserService | null,
        servicesAdditionnels,
        comptableBriques,
        poiGestion,
      });
      setTarget(updated);
      // E3 — si la cible est admin, on enregistre son périmètre.
      // Aucune brique cochée = admin complet (briques: null). Une ou plusieurs =
      // admin restreint. On ne le fait PAS sur soi-même (le back le refuserait :
      // un admin ne peut pas se restreindre lui-même).
      if (role === 'admin' && updated.id !== actor?.id) {
        const briques = adminBriques.length > 0 ? adminBriques : null;
        await api.admin.perimetres.set(id, {
          briques,
          vueMaire: adminVueMaire,
          parametres: adminParametres,
        });
      }
      notify('Modifications enregistrées.');
    } catch (err) {
      handleError(err);
    } finally {
      setSaving(false);
    }
  }

  async function handleSuspend() {
    if (!id) return;
    try {
      const u = await api.admin.users.suspend(id);
      setTarget(u);
      notify('Compte suspendu.');
    } catch (err) {
      handleError(err);
    }
  }

  async function handleReactivate() {
    if (!id) return;
    try {
      const u = await api.admin.users.reactivate(id);
      setTarget(u);
      notify('Compte réactivé.');
    } catch (err) {
      handleError(err);
    }
  }

  async function handleUnlock() {
    if (!id) return;
    try {
      const u = await api.admin.users.unlock(id);
      setTarget(u);
      notify('Compte déverrouillé. Compteur de tentatives échouées remis à zéro.');
    } catch (err) {
      handleError(err);
    }
  }

  async function handleForceReset() {
    if (!id) return;
    try {
      const res = await api.admin.users.forcePasswordReset(id);
      notify(
        res.emailSent
          ? `Email de réinitialisation envoyé à ${target?.email}.`
          : 'Token de réinitialisation posé, mais l’envoi du mail a échoué (à vérifier côté SMTP).'
      );
    } catch (err) {
      handleError(err);
    }
  }

  async function handleDelete() {
    if (!id) return;
    try {
      await api.admin.users.delete(id);
      navigate('/admin/utilisateurs', { replace: true });
    } catch (err) {
      handleError(err);
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-tech border-t-transparent"
          role="status"
          aria-label="Chargement"
        />
      </div>
    );
  }

  if (!target) {
    return (
      <div className="animate-fade-in space-y-4">
        <Alert variant="error" icon={AlertCircle}>
          {error ?? 'Utilisateur introuvable.'}
        </Alert>
        <Link
          to="/admin/utilisateurs"
          className="inline-flex items-center gap-1 text-sm font-medium text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-md px-1"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Retour à la liste
        </Link>
      </div>
    );
  }

  const isSelf = actor?.id === target.id;
  const isLocked = target.lockedUntil && new Date(target.lockedUntil) > new Date();
  const isDeleted = !!target.deletedAt;
  const isSuperAdminTarget = target.role === 'super_admin';
  const actorIsSuperAdmin = actor?.role === 'super_admin';
  const canModifyTarget = !isSelf && !isDeleted && (actorIsSuperAdmin || !isSuperAdminTarget);
  const canEdit = actor?.role === 'super_admin' || actor?.role === 'admin';

  return (
    <section aria-labelledby="user-title" className="animate-fade-in">
      <Link
        to="/admin/utilisateurs"
        className="mb-4 inline-flex items-center gap-1 rounded-r-md px-1 text-sm font-medium text-ink-muted transition-colors hover:text-tech-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Liste des utilisateurs
      </Link>

      <header className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-3">
          <LogoMark icon={UserCog} accent="tech" size="lg" />
          <div>
            <SectionEyebrow as="div">Administration · utilisateur</SectionEyebrow>
            <h1 id="user-title" className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
              {target.prenom} {target.nom}
            </h1>
            <p className="mt-0.5 flex items-center gap-2 text-sm text-ink-muted">
              <Badge variant={userAccent(target)} size="sm">
                {userBadgeLabel(target)}
              </Badge>
              <span>· {target.email}</span>
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {isDeleted && <Badge variant="neutral">Supprimé / anonymisé</Badge>}
          {!isDeleted && !target.actif && <Badge variant="warning">Suspendu</Badge>}
          {isLocked && <Badge variant="warning">Verrouillé</Badge>}
          {!target.emailVerified && <Badge variant="neutral">Email non vérifié</Badge>}
        </div>
      </header>

      {error && (
        <Alert variant="error" icon={AlertCircle} className="mb-4">
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" icon={CheckCircle2} className="mb-4">
          {success}
        </Alert>
      )}

      {isSelf && (
        <Alert variant="info" icon={AlertCircle} className="mb-4">
          C'est votre propre compte. Les actions admin sont désactivées — utilisez la page « Mon
          compte » pour modifier vos informations.
        </Alert>
      )}

      {/* Édition profil */}
      <Card variant="glass" padded className="mb-4">
        <SectionEyebrow as="h2" className="mb-3">Profil</SectionEyebrow>
        <form onSubmit={handleSave} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Prénom" htmlFor="prenom" required>
              <Input
                id="prenom"
                value={prenom}
                onChange={(e) => setPrenom(e.target.value)}
                disabled={!canEdit || !canModifyTarget}
              />
            </Field>
            <Field label="Nom" htmlFor="nom" required>
              <Input
                id="nom"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                disabled={!canEdit || !canModifyTarget}
              />
            </Field>
            <Field
              label="Email"
              htmlFor="email"
              required
              hint={actorIsSuperAdmin
                ? "Identifiant de connexion — secours super_admin, changement immédiat et tracé."
                : "Identifiant de connexion — modifiable uniquement par un super_admin (secours). L'utilisateur peut le changer lui-même depuis « Mon compte »."}
            >
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!canModifyTarget || !actorIsSuperAdmin}
              />
            </Field>
            <Field label="Téléphone" htmlFor="telephone">
              <Input
                id="telephone"
                type="tel"
                value={telephone}
                onChange={(e) => setTelephone(e.target.value)}
                disabled={!canEdit || !canModifyTarget}
              />
            </Field>
            <Field
              label="Rôle"
              htmlFor="role"
              hint={!actorIsSuperAdmin ? 'La promotion en super_admin est réservée aux super_admin.' : undefined}
            >
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value as UserRole)}
                disabled={!canEdit || !canModifyTarget}
                className={SELECT_CLS}
              >
                {ALL_ROLES.map((r) => (
                  <option
                    key={r}
                    value={r}
                    disabled={r === 'super_admin' && !actorIsSuperAdmin}
                  >
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
            </Field>
            {role !== 'admin' && role !== 'super_admin' && (
            <Field
              label="Service"
              htmlFor="service"
              hint="Obligatoire pour les managers et agents ; laisser « Aucun » pour les maires, adjoints et administrés."
            >
              <select
                id="service"
                value={service}
                onChange={(e) => setService(e.target.value)}
                disabled={!canEdit || !canModifyTarget}
                className={SELECT_CLS}
              >
                {SERVICE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Field>
            )}

            {/* E3 — Périmètre d'administration (multi-briques) pour un admin.
                Aucune case = admin complet (maire). Une ou plusieurs = restreint. */}
            {role === 'admin' && !isSelf && (
              <Field
                label="Périmètre d'administration (briques gérées)"
                htmlFor="admin-briques"
                hint="Cochez les services que cet administrateur gère. Aucune case = administrateur complet (comme le maire). Une ou plusieurs cases = administrateur restreint à ces services uniquement."
              >
                <div id="admin-briques" className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {SERVICE_OPTIONS.filter((o) => o.value).map((opt) => {
                    const checked = adminBriques.includes(opt.value);
                    return (
                      <label key={opt.value} className="flex items-center gap-2 text-sm text-ink-muted">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!canEdit || !canModifyTarget}
                          onChange={(e) => {
                            setAdminBriques((prev) => {
                              const next = e.target.checked
                                ? [...prev, opt.value]
                                : prev.filter((s) => s !== opt.value);
                              // Dès qu'au moins une brique est cochée (= admin
                              // devient restreint), on retire par défaut l'accès
                              // à la vue Maire et aux paramètres mairie.
                              if (next.length > 0 && prev.length === 0) {
                                setAdminVueMaire(false);
                                setAdminParametres(false);
                              }
                              // Retour à « admin complet » : on rend tout.
                              if (next.length === 0) {
                                setAdminVueMaire(true);
                                setAdminParametres(true);
                              }
                              return next;
                            });
                          }}
                        />
                        {opt.label}
                      </label>
                    );
                  })}
                </div>
                {adminBriques.length > 0 && (
                  <div className="mt-3 space-y-1.5 rounded-r-lg border border-border bg-surface/40 p-2.5">
                    <p className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
                      Accès transverses (décochés par défaut pour un admin restreint)
                    </p>
                    <label className="flex items-center gap-2 text-sm text-ink-muted">
                      <input
                        type="checkbox"
                        checked={adminVueMaire}
                        disabled={!canEdit || !canModifyTarget}
                        onChange={(e) => setAdminVueMaire(e.target.checked)}
                      />
                      Accès à la vue Maire consolidée
                    </label>
                    <label className="flex items-center gap-2 text-sm text-ink-muted">
                      <input
                        type="checkbox"
                        checked={adminParametres}
                        disabled={!canEdit || !canModifyTarget}
                        onChange={(e) => setAdminParametres(e.target.checked)}
                      />
                      Accès aux paramètres de la commune
                    </label>
                  </div>
                )}
                {isSelf && (
                  <p className="mt-1 text-2xs text-ink-subtle">
                    Vous ne pouvez pas modifier votre propre périmètre.
                  </p>
                )}
              </Field>
            )}

            {/* Services additionnels (cumul) — pertinent pour manager / agent */}
            {(role === 'manager' || role === 'agent') && (
              <Field
                label="Services additionnels (cumul)"
                htmlFor="services-add"
                hint="Cochez d'autres services que cette personne gère aussi, avec le même rôle. Utile pour les petites mairies (ex. Associations + Cantine)."
              >
                <div id="services-add" className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {SERVICE_OPTIONS.filter((o) => o.value && o.value !== service).map((opt) => {
                    const checked = servicesAdditionnels.includes(opt.value as UserService);
                    return (
                      <label key={opt.value} className="flex items-center gap-2 text-sm text-ink-muted">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!canEdit || !canModifyTarget}
                          onChange={(e) => {
                            setServicesAdditionnels((prev) =>
                              e.target.checked
                                ? [...prev, opt.value as UserService]
                                : prev.filter((s) => s !== opt.value)
                            );
                          }}
                        />
                        {opt.label}
                      </label>
                    );
                  })}
                </div>
              </Field>
            )}

            <Field
              label="Attribut comptable"
              htmlFor="comptable-grc"
              hint="Donne le droit d'encaisser/annuler les factures de la brique cochée, sans changer le rôle. Cloisonné : GRC ≠ RH."
            >
              <div className="flex flex-wrap gap-4 pt-1">
                <label className="inline-flex items-center gap-2 text-sm text-ink">
                  <input
                    id="comptable-grc"
                    type="checkbox"
                    checked={comptableBriques.includes('grc')}
                    disabled={!canEdit || !canModifyTarget}
                    onChange={(e) => setComptableBriques((prev) => e.target.checked ? [...new Set([...prev, 'grc' as const])] : prev.filter((b) => b !== 'grc'))}
                    className="h-4 w-4 accent-[oklch(var(--c-grc,var(--c-primary)))]"
                  />
                  Comptable GRC (facturation)
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={comptableBriques.includes('rh')}
                    disabled={!canEdit || !canModifyTarget}
                    onChange={(e) => setComptableBriques((prev) => e.target.checked ? [...new Set([...prev, 'rh' as const])] : prev.filter((b) => b !== 'rh'))}
                    className="h-4 w-4 accent-[oklch(var(--c-rh,var(--c-primary)))]"
                  />
                  Comptable RH (paie)
                </label>
              </div>
            </Field>

            <Field
              label="Points d'intérêt (Tourisme & Entraide)"
              htmlFor="poi-gestion"
              hint="Autorise cet agent/manager GRC à créer et modifier les points d'intérêt sur la carte. Sans cette case, il est en lecture seule."
            >
              <label className="inline-flex items-center gap-2 pt-1 text-sm text-ink">
                <input
                  id="poi-gestion"
                  type="checkbox"
                  checked={poiGestion}
                  disabled={!canEdit || !canModifyTarget}
                  onChange={(e) => setPoiGestion(e.target.checked)}
                  className="h-4 w-4 accent-[oklch(var(--c-grc,var(--c-primary)))]"
                />
                Peut gérer les points d'intérêt
              </label>
            </Field>
          </div>
          {canEdit && canModifyTarget && (
            <div className="flex justify-end pt-2">
              <Button type="submit" variant="primary" disabled={saving}>
                <Save className="h-4 w-4" aria-hidden="true" />
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </div>
          )}
        </form>
      </Card>

      {/* Actions */}
      {canEdit && canModifyTarget && (
        <Card variant="glass" padded className="mb-4">
          <SectionEyebrow as="h2" className="mb-3">Actions</SectionEyebrow>
          <div className="flex flex-wrap gap-2">
            {target.actif ? (
              <Button variant="secondary" size="sm" onClick={handleSuspend}>
                <Pause className="h-4 w-4" aria-hidden="true" /> Suspendre le compte
              </Button>
            ) : (
              <Button variant="success" size="sm" onClick={handleReactivate}>
                <Play className="h-4 w-4" aria-hidden="true" /> Réactiver le compte
              </Button>
            )}
            {isLocked && (
              <Button variant="secondary" size="sm" onClick={handleUnlock}>
                <Unlock className="h-4 w-4" aria-hidden="true" /> Déverrouiller
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={handleForceReset}>
              <KeyRound className="h-4 w-4" aria-hidden="true" /> Forcer reset du mot de passe
            </Button>
            {/* L13.13 — Changer le rôle (temporaire ou définitif).
                L13.14 — visible uniquement pour un admin/super_admin NATIF. */}
            {canGrantRoles && (
              <Link
                to={`/admin/utilisateurs/${target.id}/role-override`}
                className="inline-flex items-center gap-1.5 h-9 rounded-r-md border border-border bg-surface/60 px-3 text-sm font-medium text-ink shadow-soft transition-all hover:border-tech/40 hover:bg-tech/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              >
                <UserCog className="h-4 w-4" aria-hidden="true" /> Changer le rôle (temporaire / définitif)
              </Link>
            )}
          </div>
        </Card>
      )}

      {/* Suppression RGPD */}
      {canEdit && canModifyTarget && !isDeleted && (
        <Card variant="glass" padded>
          <SectionEyebrow as="h2" className="mb-2 text-urgent">Zone dangereuse</SectionEyebrow>
          <p className="text-sm text-ink-muted leading-relaxed">
            La suppression anonymise les données personnelles (email, prénom, nom, téléphone) de
            manière irréversible et désactive le compte. L'identifiant interne est conservé pour
            l'intégrité de l'audit log et des données métier liées (signalements, etc.).
          </p>
          {confirmDelete ? (
            <Alert variant="warning" icon={AlertTriangle} className="mt-3">
              <div>
                <p className="font-medium">Confirmer la suppression RGPD ?</p>
                <p className="mt-1 text-sm">
                  Cette action est <strong>irréversible</strong>.
                </p>
                <div className="mt-3 flex gap-2">
                  <Button variant="danger" size="sm" onClick={handleDelete}>
                    Oui, supprimer et anonymiser
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => setConfirmDelete(false)}>
                    Annuler
                  </Button>
                </div>
              </div>
            </Alert>
          ) : (
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)} className="mt-3">
              <Trash2 className="h-4 w-4" aria-hidden="true" /> Supprimer le compte
            </Button>
          )}
        </Card>
      )}
    </section>
  );
}
